import moment from "moment";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
export const lastLoginBodyTemplate = (rowData) => {
  if (!rowData.last_login_at) {
    return "--";
  }
  const lastLoginTime = moment.utc(rowData.last_login_at).local();
  const currentTime = moment().local();
  const isCurrentlyLoggedIn = currentTime.diff(lastLoginTime, "seconds") <= 5;

  return isCurrentlyLoggedIn
    ? "Currently Logged In"
    : lastLoginTime.format("DD/MM/YYYY h:mm:ss A");
};

export const validateEmail = (email) => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
};

export const validatePassword = (password) => {
  const regex = /^(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;
  return regex.test(password);
};

export const preventNegativeValue = (value) => {
  return value >= 0;
};

export const limitToTwoDecimalPlaces = (value) => {
  return value.match(/^\d*(\.\d{0,2})?$/);
};

export function formatNumber(x) {
  if (isNaN(x)) return x;

  const isNegative = x < 0;
  const absoluteValue = Math.abs(x).toString();
  const parts = absoluteValue.split(".");

  let integerPart = parts[0];

  const reversed = integerPart.split("").reverse();

  let formatted = "";
  for (let i = 0; i < reversed.length; i++) {
    if (i > 0 && i % 3 === 0) {
      formatted += "'";
    }
    formatted += reversed[i];
  }

  formatted = formatted.split("").reverse().join("");

  const result = parts.length > 1 ? `${formatted}.${parts[1]}` : formatted;

  return isNegative ? `-${result}` : result;
}

export const truncateText = (text, maxChars) => {
  if (text.length > maxChars) {
    return text.slice(0, maxChars) + " ...";
  }
  return text;
};

export function truncateToTwoDecimals(value) {
  if (isNaN(value)) return "-";
  return (Math.floor(value * 100) / 100).toFixed(2);
}

export function TwoDecimalsWithoutRounding(value) {
  if (isNaN(value)) return "-";
  return Math.floor(value * 100) / 100;
}

export const HorizontalTabButtons = ({ buttons = [], selected, onSelect }) => {
  const navigate = useNavigate();

  const handleClick = (btn) => {
    onSelect(btn);
    if (btn === "Fortune") {
      navigate("/");
    }
  };
  return (
    <div className="flex gap-5 items-baseline md:ml-40 overflow-y-auto w-[calc(100%-50px)] whitespace-nowrap">
      {buttons.map((btn) => (
        <button
          key={btn}
          className={
            selected === btn
              ? "text-lightPrimary dark:text-primary-gradient font-normal text-lg"
              : "text-info dark:text-grey font-normal text-sm"
          }
          onClick={() => handleClick(btn)}
        >
          {btn}
        </button>
      ))}
    </div>
  );
};

export const getTabLabelsByAssetType = (assetType) => {
  switch (assetType) {
    case "1":
      return ["Fortune", "Movements"];

    case "2":
      return ["Fortune", "Return", "Movements"];

    case "3":
      return ["Fortune", "Return", "Movements", "Pictures"];
    case "4":
      return [
        "Fortune",
        "Pictures",
        "Documents",
        "Movements",
        "Journal",
        "Legacy",
      ];

    case "5":
      return ["Fortune", "Return", "Documents", "Movements"];

    case "6":
      return ["Fortune", "Return", "Documents"];

    case "7":
      return [
        "Fortune",
        "Details",
        "Financial Profile",
        "Look for",
        "Legacy",
        "Insider",
      ];

    default:
      return ["Fortune", "Return"];
  }
};

export const getDropdownLabelsByAssetType = (assetType) => {
  const toOptionArray = (labels) => labels.map((label) => ({ name: label }));

  switch (assetType) {
    case "1":
    case "2":
      return toOptionArray(["INCOME", "EXPENSE", "IN", "OUT"]);

    case "3":
      return toOptionArray(["BUY", "EXPENSE", "INCOME", "SELL", "VALUATION"]);

    case "4":
      return toOptionArray([
        // "ADD ITEM",
        "EXPENSE",
        "INCOME",
        "SELL",
        "VALUATION",
      ]);

    case "5":
      return toOptionArray(["EXPENSE", "INCOME", "SELL", "VALUATION"]);

    default:
      return toOptionArray(["INCOME", "EXPENSE"]);
  }
};

export const getBreadcrumbLabelsByAssetType = (assetType) => {
  switch (assetType) {
    case "Cash":
    case "Stock":
    case "Real Estate":
    case "Luxury Assets":
    case "Company":
    case "Pension":
      return [
        { label: "Home", path: "/" },
        { label: assetType, path: "#" },
      ];
    default:
      return [
        { label: "Home", path: "/" },
        { label: assetType || "Unknown", path: "#" },
      ];
  }
};

export function formatFlexibleDecimalNumber(value) {
  if (!value) return "";

  // Remove invalid characters
  const cleaned = value.replace(/[^0-9.\-]/g, "");

  // Handle edge cases: just '-', '.', '-.'
  if (cleaned === "-" || cleaned === "." || cleaned === "-.") {
    return cleaned;
  }

  // If multiple dots, ignore formatting
  if ((cleaned.match(/\./g) || []).length > 1) {
    return value;
  }

  const isNegative = cleaned.startsWith("-");
  const [intPart, decimalPart] = cleaned.replace("-", "").split(".");

  // Format integer with apostrophes
  const formattedInt = intPart
    .split("")
    .reverse()
    .reduce((acc, digit, i) => {
      if (i > 0 && i % 3 === 0) acc.push("'");
      acc.push(digit);
      return acc;
    }, [])
    .reverse()
    .join("");

  let result = isNegative ? "-" + formattedInt : formattedInt;
  if (decimalPart !== undefined) result += "." + decimalPart;

  return result;
}

export const cleanNumberInput = (val) => {
  return val?.toString().replace(/[^0-9.-]/g, "");
};

// utils/formatRoundedCHF.js
export const formatRoundedCHF = (value) => {
  const numericValue = Number(value);

  if (isNaN(numericValue)) return "0";

  const rounded = Math.round(numericValue);

  // Format with apostrophe (') separator
  const formatted = rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, "'");

  return formatted;
};

export const AssetCard = ({
  name,
  value,
  roi,
  legacy,
  asset_type,
  icon = null,
  onClick,
  isSold = false,
}) => {
  const { currency } = useSelector((state) => state.user);
  return (
    <div
      className="flex justify-between cursor-pointer items-start border border-lightPrimary/40 dark:border-info/40 p-3 rounded-sm w-full dark:bg-card-custom-radials bg-[length:400%_200%] text-info dark:text-grey text-[13px] lg:text-sm bg-white min-h-[180px]"
      onClick={onClick}
    >
      <div className="flex flex-col gap-1 pr-2 w-fit">
        <div className="font-semibold text-lightPrimary dark:text-primary-gradient line-clamp-2 h-[40px] pt-1">
          {name}{" "}
          {isSold && (
            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-500 text-white ml-2">
              SOLD
            </span>
          )}
        </div>
        <div className="flex mb-2 word-break w-full max-w-[22rem] gap-7 mb-3">
          <span className="font-medium">Value:</span>{" "}
          <span className="text-lightPrimary dark:text-primary-gradient">
            {value} {currency || ""}
          </span>
        </div>

        <div className="flex mb-4 w-full word-break max-w-[22rem] gap-10">
          <span className="font-medium">ROI:</span>{" "}
          <span
            className={`${
              parseFloat(roi ?? 0) < 0
                ? "text-expense dark:text-warning"
                : "text-lightPrimary dark:text-primary-gradient"
            }`}
          >
            {roi === "--" || roi === null || roi === undefined
              ? roi
              : `${formatNumber(
                  truncateToTwoDecimals(Math.abs(Number(roi ?? 0)))
                )}%`}
          </span>
        </div>

        {legacy && (
          <div className="flex flex-wrap w-full max-w-[20rem] gap-5 mt-3">
            <span className="font-medium">Legacy:</span>{" "}
            <span className="text-lightPrimary dark:text-primary-gradient">
              {legacy || "-"}
            </span>
          </div>
        )}
      </div>
      <div className="flex justify-center text-primary dark:text-white w-48 h-28">
        {<img src={icon} /> || (
          <span className="text-xs text-muted">No Icon</span>
        )}
      </div>
    </div>
  );
};

const AssetCardSkeleton = () => {
  return (
    <div className="flex items-start border border-lightPrimary/40 dark:border-info/40 p-3 rounded-md w-full dark:bg-card-custom-radial animate-pulse min-h-[200px]">
      <div className="flex flex-col gap-6 pr-2 w-full">
        <div className="h-4 w-2/3 bg-gray-200 dark:bg-gray-700 rounded" />
        <div className="h-3 w-1/2 bg-gray-200 dark:bg-gray-700 rounded" />
        <div className="h-3 w-1/3 bg-gray-200 dark:bg-gray-700 rounded mb-6" />
        <div className="h-3 w-1/3 bg-gray-200 dark:bg-gray-700 rounded" />
      </div>
      <div className="w-24 h-24 bg-gray-200 dark:bg-gray-700 rounded-md" />
    </div>
  );
};
export default AssetCardSkeleton;
